import { ICPLAuthUsersClient, UserModel } from "services/api/cplauthapi/CPLAuthAPI";
import { inject, injectable } from "inversify";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { ISessionService } from "./SessionService";

export interface IAuthUsersService {
  getUserProfile(userId: string | undefined): Promise<UserModel>;
  updateUserProfile(userModel: UserModel): Promise<void>;
  getUserByToken(token: string): Promise<UserModel>;
  getProfile(): Promise<UserModel>;
  updateUserActiveStatus(userID: string): Promise<void>;
  verifyEmail(userID: string, verificationCode: string): Promise<void>;
  resendVerificationEmail(userID: string): Promise<void>;
  changePassword(userID: string, newPassword: string): Promise<void>;
  changeProfilePassword(newPassword: string): Promise<void>;
  passwordReset(email: string): Promise<void>;
}

@injectable()
export class AuthUsersService implements IAuthUsersService {
  @inject(SERVICE_TYPES.CPLAuthUsersClient) cplAuthUsersClient!: ICPLAuthUsersClient;
  @inject(SERVICE_TYPES.SessionService) sessionService!: ISessionService;

  getProfile(): Promise<UserModel> {
    return this.cplAuthUsersClient.getProfile();
  }

  getUserProfile(userId: string | undefined): Promise<UserModel> {
    return this.cplAuthUsersClient.getUser(userId);
  }

  updateUserActiveStatus(userID: string): Promise<void> {
    return this.cplAuthUsersClient.updateUserActiveStatus(userID);
  }

  async getUserByToken(token: string): Promise<UserModel> {
    if (token) {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );

      const tokenObject = JSON.parse(jsonPayload);
      const userModel = await this.cplAuthUsersClient.getUser(tokenObject.userid);
      return userModel;
    } else {
      throw new Error("Invalid token");
    }
  }

  updateUserProfile(userModel: UserModel): Promise<void> {
    return this.cplAuthUsersClient.updateUser(userModel);
  }

  verifyEmail(userID: string, verificationCode: string): Promise<void> {
    return this.cplAuthUsersClient.verifyEmail(userID, verificationCode);
  }

  resendVerificationEmail(userID: string): Promise<void> {
    return this.cplAuthUsersClient.resendVerificationCode(userID);
  }

  changePassword(userID: string, newPassword: string): Promise<void> {
    return this.cplAuthUsersClient.changeUserPassword(userID, newPassword);
  }

  changeProfilePassword(newPassword: string): Promise<void> {
    return this.cplAuthUsersClient.changeProfilePassword(newPassword);
  }

  passwordReset(emailAddress: string): Promise<void> {
    return this.cplAuthUsersClient.passwordReset(emailAddress);
  }
}
